import React from 'react';
import { Form, Formik } from 'formik';
import { Grid } from '@mui/material';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '@mui/lab';
import { AccountData, AccountService, LegalSettingsData } from '../api';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';

type LegalSettingsPageProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export const LegalSettingsPage = ({ account, updateLocalAccount, setIsUpdatedSnackOpen }: LegalSettingsPageProps) => {
  const { t } = useTranslation();

  const initialValues: LegalSettingsData = {
    terms_conditions_link: account?.terms_conditions_link || '',
    privacy_policy_link: account?.privacy_policy_link || '',
  };

  const validationSchema = yup.object().shape({
    terms_conditions_link: yup.string().url(t('accountSettings.invalidUrl')),
    privacy_policy_link: yup.string().url(t('accountSettings.invalidUrl')),
  });

  const onSubmit = async (
    values: LegalSettingsData,
    {
      setSubmitting,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
    },
  ) => {
    try {
      setSubmitting(true);
      const updatedSettings = await AccountService.updateLegalSettings(values);
      await updateLocalAccount({
        ...account,
        terms_conditions_link: updatedSettings.terms_conditions_link,
        privacy_policy_link: updatedSettings.privacy_policy_link,
      });
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ isSubmitting, dirty }) => (
        <Form>
          <Grid container p={0} flexWrap="nowrap" alignItems="flex-start" width="100%">
            <Grid container flexDirection="column" gap={2} xs={9}>
              <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4">{t('accountSettings.legalSettings')}</Typography>
              </Grid>

              <Grid item mb={2}>
                <Typography variant="body1" color="grey">
                  {t('accountSettings.legalSettingsDescription')}
                </Typography>
              </Grid>

              <Grid item>
                <FormTextField
                  isFieldUpdate
                  width={400}
                  fieldName="terms_conditions_link"
                  type="url"
                  label={t('accountSettings.termsConditionsLink')}
                  disabled={false}
                />
              </Grid>

              <Grid item>
                <FormTextField
                  isFieldUpdate
                  width={400}
                  fieldName="privacy_policy_link"
                  type="url"
                  label={t('accountSettings.privacyPolicyLink')}
                  disabled={false}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" mt={2} xs={2}>
              <LoadingButton
                loading={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty}>
                {t('save')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
